import { useCallback } from 'react';
import { useConnectWallet, useCurrentAccount, useCurrentWallet, useWallets } from '@mysten/dapp-kit';
import type { WalletAccount, WalletWithRequiredFeatures } from '@mysten/wallet-standard';

import { useWalletStore } from './useWalletStore';
import { findWalletByIdentifier, getWalletUniqueIdentifier } from '../utils/wallet.util';
import { WalletSliceKey } from '../types/wallet-slice.type';
import { useSuiWallet } from './useSuiWallet';
import { useZkLoginWallet } from './useZkLoginWallet';
import { isEnokiWallet } from '@mysten/enoki';

export function useWalletConnectionResolver() {
	const wallets = useWallets();
	const { mutateAsync: connectWallet } = useConnectWallet();
	const { currentWallet: sdkWallet } = useCurrentWallet();
	const sdkAccount = useCurrentAccount();

	const suiWalletSlice = useSuiWallet();
	const zkLoginWalletSlice = useZkLoginWallet();
	const setConnectionStatus = useWalletStore((state) => state.setConnectionStatus);
	const setWalletConnected = useWalletStore((state) => state.setWalletConnected);

	return useCallback(
		async (walletType: WalletSliceKey): Promise<{ wallet: WalletWithRequiredFeatures; account: WalletAccount }> => {
			const slice = walletType === 'suiWallet' ? suiWalletSlice : zkLoginWalletSlice;
			const persistedIdentifier = (slice.currentWallet && getWalletUniqueIdentifier(slice.currentWallet)) ?? slice.lastConnectedWalletName;
			const persistedAccountAddress = slice.currentAccount?.address ?? slice.lastConnectedAccountAddress ?? slice.accounts?.[0]?.address ?? null;

			const sdkMatchesWalletType =
				sdkWallet && sdkAccount ? (walletType === 'zkLoginWallet' ? isEnokiWallet(sdkWallet) : !isEnokiWallet(sdkWallet)) : false;
			const fallbackWallet = sdkMatchesWalletType ? sdkWallet : null;
			const fallbackAccount = fallbackWallet ? sdkAccount : null;
			const fallbackIdentifier = fallbackWallet ? getWalletUniqueIdentifier(fallbackWallet) : null;

			const resolvedIdentifier = persistedIdentifier ?? fallbackIdentifier;
			const resolvedAccountAddress = persistedAccountAddress ?? fallbackAccount?.address ?? null;

			if (!resolvedIdentifier || !resolvedAccountAddress) {
				throw new Error(`No persisted ${walletType} connection available.`);
			}

			if (fallbackWallet && fallbackAccount && fallbackIdentifier === resolvedIdentifier && fallbackAccount.address === resolvedAccountAddress) {
				setWalletConnected(walletType, fallbackWallet, fallbackWallet.accounts, fallbackAccount);
				return { wallet: fallbackWallet, account: fallbackAccount };
			}

			const targetWallet = findWalletByIdentifier(wallets, resolvedIdentifier);

			if (!targetWallet) {
				throw new Error(`Wallet ${resolvedIdentifier} is not available.`);
			}

			setConnectionStatus(walletType, 'connecting');

			try {
				const { accounts } = await connectWallet({
					wallet: targetWallet,
					accountAddress: resolvedAccountAddress,
					silent: true,
				});

				const selectedAccount = accounts.find((account) => account.address === resolvedAccountAddress) ?? accounts[0] ?? null;

				if (!selectedAccount) {
					throw new Error(`No account available for wallet ${resolvedIdentifier}`);
				}

				setWalletConnected(walletType, targetWallet, accounts, selectedAccount);
				setConnectionStatus(walletType, 'connected');
				return { wallet: targetWallet, account: selectedAccount };
			} catch (error) {
				setConnectionStatus(walletType, 'disconnected');
				throw error;
			}
		},
		[connectWallet, sdkAccount, sdkWallet, setConnectionStatus, setWalletConnected, suiWalletSlice, wallets, zkLoginWalletSlice],
	);
}
